import { AxiosResponse } from 'axios';
import { addMessage, clearMessages } from '../../store/ancillary/actions';
import { RESPONSE_CODES } from './_constants';

/**
 * Internal helper function for constructing errors specific to @param response. Allows for
 * distinction in error handling based on response code of @param response.
 *
 * @function
 * @param {AxiosResponse} response HTTP axios response object with `IMSPacket` data.
 * @returns {Error} response-specific error.
 */
const createResponseError = response => {
	let message = null;

	switch (response.data.responseCode) {
		case RESPONSE_CODES.INVALID:
			message = 'Success';
			break;
		case RESPONSE_CODES.TEAPOT:
			message = 'Refusal to brew coffee';
			break;
		default:
			message = 'Error';
			break;
	}

	return new Error(
		`HTTP ${message}: status code ${response.data.responseCode}`,
		{
			cause: new Error(
				response.data.messages.length > 0
					? response.data.messages[0].value ||
					  response.data.messages[0].Value
					: response.data.payload
					? response.data.payload
					: `${message} occurred with response code ${response.data.responseCode}`
			),
		}
	);
};

/**
 * Helper function for processing HTTP responses. Raises errors if response unsuccessful (non-200), message banners
 * if available.
 *
 * @function
 * @param {AxiosResponse} response HTTP axios response object with `IMSPacket` data.
 * @param {Function} callback nullable function to execute and return on successful response.
 * @param {Function} dispatch nullable forwarded Redux store's method for dispatching actions.
 * @returns {any} return object of callback.
 */
export const then = (response, callback = null, dispatch = null) => {
	if (response && response.status) {
		switch (response.status) {
			case 200:
				if (response.data) {
					if (
						dispatch &&
						response.data.messages &&
						response.data.messages.length > 0
					) {
						dispatch(clearMessages());
						response.data.messages.forEach(message =>
							dispatch(addMessage(message))
						);
					}
					if (callback && response.data.responseCode) {
						switch (response.data.responseCode) {
							case RESPONSE_CODES.OK:
								return callback(response.data.payload);
							default:
								throw createResponseError(response);
						}
					} else {
						return response;
					}
				}
				break;
			default:
				break;
		}
	}
};

/**
 * Processes raised axios errors.
 *
 * @function
 * @param {Error} error raised error.
 * @returns {Error} processed error.
 */
export const catchAxiosError = error => {
	if (error.isAxiosError) {
		let message = error.message;
		if (error.response) {
			switch (error.response.status) {
				case RESPONSE_CODES.UNAUTHORIZED:
					message = 'Unauthorized client.';
					break;
				case RESPONSE_CODES.FORBIDDEN:
					message = 'Forbidden client.';
					break;
				case RESPONSE_CODES.NOT_FOUND:
					message = 'Resource not found.';
					break;
				default:
					break;
			}
		}
		return new Error(message);
	} else {
		return error;
	}
};
